<script lang="ts" setup>
import { computed, onMounted, onUnmounted } from 'vue';
import AdpDocumentCard from '@/components/common/adp-document-card.vue';
import BasicEmptyFeed from '@/components/common/basic-empty-feed.vue';
import { useHomeSectionViewTracking } from '@/components/home/use-home-section-view-tracking';
import { Article } from '@/types';
import { STATES, useIndustries, useIndustryArticles } from '@/utils/swr';
import { useEmitter, useStore } from '@/utils/vue';

const layoutVariant = 'legacy';

const store = useStore();
const emitter = useEmitter();
const { data } = useIndustries();

const industries = computed(() => {
  return data.value?.industries ?? [];
});

const userInfo = computed(() => store.state.userInfo.userInfo);

const currentIndustry = computed(() =>
  industries.value.find(i => i.id === userInfo.value?.industry_id),
);
const currentIndustryId = computed(() => currentIndustry.value?.id);

const { data: industryNews, state } = useIndustryArticles(currentIndustryId, 1);
const isLoading = computed(() => state.value === STATES.PENDING);

const articles = computed(() =>
  (industryNews.value?.industry_articles ?? []).slice(0, 5),
);

const { setRef, requestId } = useHomeSectionViewTracking(
  'industry_news_view',
  () => !isLoading.value,
  () => ({
    document_count: articles.value.length,
    layout_variant: layoutVariant,
  }),
);

const handleArticleUpdate = (article: Article) => {
  if (articles.value === null) {
    return;
  }
  const index = articles.value.findIndex(a => a.id === article.id);
  if (index >= 0) {
    articles.value[index].marks = article.marks;
  }
};

const getIndustryTrackingData = (topicId: number) => {
  return {
    id: currentIndustryId.value ?? 0,
    topic: { id: topicId, rank: 1 },
  };
};

onMounted(async () => {
  emitter.on('article-updated', handleArticleUpdate);
});

onUnmounted(async () => {
  emitter.off('article-updated', handleArticleUpdate);
});

const noFeedMessage = computed(() =>
  currentIndustryId.value === undefined
    ? '現在、業界が未選択になっています。'
    : 'この業界のニュースはありませんでした。',
);
</script>

<template>
  <div class="industry-news-preview" :ref="setRef">
    <div class="title">
      <div class="c-text-regular--l">業界ニュース</div>
      <div class="industry-name c-text-regular--m" v-if="currentIndustry">
        {{ `・「${currentIndustry.name}」を選択中` }}
      </div>
    </div>
    <div
      class="preview-articles"
      v-if="currentIndustry && articles && articles.length > 0"
    >
      <div class="articles" v-for="article in articles" :key="article.id">
        <div class="spacing-12"></div>
        <AdpDocumentCard
          page-name="home"
          feature="industry_news"
          :adp-document="article"
          :show-comment-count="1"
          :case-study-text="article.case_study_text"
          :trend-company-text="article.trend_company_text"
          :industry-data="getIndustryTrackingData(article.topic_id)"
          :disable-related-articles="false"
          width="100%"
          :contents-context="{
            event_name: 'industry_news',
            parent_request_id: requestId,
          }"
        ></AdpDocumentCard>
      </div>
      <div class="spacing-16"></div>
      <div class="industry-news-page-link">
        <router-link to="/team/industry_news">
          <button class="c-btn c-btn--small c-btn--outline c-btn--auto">
            もっと見る
          </button>
        </router-link>
      </div>
    </div>
    <div
      class="empty-state"
      v-if="
        currentIndustry === undefined || (articles && articles.length === 0)
      "
    >
      <div class="spacing-12"></div>
      <BasicEmptyFeed>{{ noFeedMessage }}</BasicEmptyFeed>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.title {
  display: flex;
  align-items: center;
  .industry-name {
    color: $color-gray600;
  }
}
.industry-news-preview {
  display: flex;
  flex-direction: column;

  .industry-news-page-link {
    display: flex;
    justify-content: center;

    button {
      background: $color-white;
      &:hover {
        background: $color-gray200;
      }
    }
  }
}
</style>
